import type { StockoutReportRow } from '@stockwait/shared'
import { EmptyState, ErrorState, LoadingState } from '../components/StateViews'
import { money, percent } from '../lib/format'
import { useApi } from '../lib/useApi'

type Payload = {
  rows: StockoutReportRow[]
  periodDays: number
  totalEstimatedLostMinor: number
  currency: string
  methodology: string[]
}

/**
 * Stockout cost and trend report (SW-804).
 *
 * A different insight from the restock plan: chronic stockout says the reorder *point* is
 * wrong, not the reorder quantity. Merchants running on gut feel rarely notice a product has
 * been unavailable for 40% of the quarter.
 */
export function Stockouts() {
  const { data, error, loading, reload } = useApi<Payload>('/stockouts?days=90')

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  if (data.rows.length === 0) {
    return (
      <EmptyState heading="No stockouts recorded yet">
        We start tracking stockout duration from the moment you install, so this report fills out
        over the next few weeks.
      </EmptyState>
    )
  }

  const chronic = data.rows.filter((row) => row.chronic)

  return (
    <s-stack direction="block" gap="large">
      <s-section>
        <s-stack direction="block" gap="base">
          <s-heading>
            Estimated {money(data.totalEstimatedLostMinor, data.currency)} lost to stockouts
          </s-heading>
          <s-text color="subdued">
            Over the last {data.periodDays} days, across {data.rows.length} products. This is a
            floor, not a forecast. See the method below.
          </s-text>
        </s-stack>
      </s-section>

      {chronic.length > 0 ? (
        <s-section heading="Chronic stockouts">
          <s-stack direction="block" gap="base">
            <s-paragraph color="subdued">
              These stocked out three or more times in the period. That usually means the reorder{' '}
              <strong>point</strong> is too low, which is a different fix from ordering more.
            </s-paragraph>
            <s-stack direction="inline" gap="small-300">
              {chronic.slice(0, 12).map((row) => (
                <s-chip key={row.variantId}>
                  {row.productTitle ?? 'Product'} ({row.stockoutCount}×)
                </s-chip>
              ))}
            </s-stack>
          </s-stack>
        </s-section>
      ) : null}

      <s-section heading="By estimated cost">
        <s-table>
          <s-table-header-row>
            <s-table-header>Product</s-table-header>
            <s-table-header>Times out</s-table-header>
            <s-table-header>Days out</s-table-header>
            <s-table-header>% of period</s-table-header>
            <s-table-header>Est. lost</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {data.rows.slice(0, 50).map((row) => (
              <s-table-row key={row.variantId}>
                <s-table-cell>
                  <s-stack direction="block">
                    <s-text type="strong">{row.productTitle ?? 'Product'}</s-text>
                    {row.variantTitle && row.variantTitle !== 'Default Title' ? (
                      <s-text color="subdued">{row.variantTitle}</s-text>
                    ) : null}
                  </s-stack>
                </s-table-cell>
                <s-table-cell>
                  {row.stockoutCount}
                  {row.chronic ? <s-badge tone="warning">Chronic</s-badge> : null}
                </s-table-cell>
                <s-table-cell>{row.totalDaysOutOfStock}</s-table-cell>
                <s-table-cell>{percent(row.shareOfPeriodOutOfStock, 0)}</s-table-cell>
                <s-table-cell>{money(row.estimatedLostRevenueMinor, row.currency)}</s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
      </s-section>

      <s-section heading="How this is estimated">
        <s-unordered-list>
          {data.methodology.map((note) => (
            <s-list-item key={note}>
              <s-text color="subdued">{note}</s-text>
            </s-list-item>
          ))}
        </s-unordered-list>
      </s-section>
    </s-stack>
  )
}
